import Vue from 'vue';
import cloneDeep from 'lodash.clonedeep';
import shortid from 'shortid';
import * as log from 'loglevel';

import layerDefaults from '@/shared/layer-defaults';
import getterHelpers from '@/store/getter-helpers'; // TODO: Refactor to use getter as second argument of other getters
import layerInit from '@/shared/layer-init';

import {
  SET_CARD,
  SET_KARTENDESIGNER_VISIBILITY,
  SET_ACTIVE_SIDE_TO_FIRST_SIDE,
  SET_ACTIVE_SIDE_BY_ID,
  SET_ACTIVE_LAYER_BY_ID,
  SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT,
  SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT_TO_NONE,
  SET_ACTIVE_LAYER_TO_NONE,
  SET_FONT_SIZE_FOR_ACTIVE_LAYER,
  SET_LINE_HEIGHT_FOR_ACTIVE_LAYER,
  SET_FONT_NAME_FOR_ACTIVE_LAYER,
  SET_FONT_WEIGHT_FOR_ACTIVE_LAYER,
  SET_FONT_STYLE_FOR_ACTIVE_LAYER,
  SET_FONT_COLOR_FOR_ACTIVE_LAYER,
  SET_TEXT_FOR_ACTIVE_LAYER,
  SET_CONTRAST_FOR_ACTIVE_LAYER,
  SET_BRIGHTNESS_FOR_ACTIVE_LAYER,
  SET_SCALE_FOR_ACTIVE_LAYER,
  SET_ROTATION_FOR_ACTIVE_LAYER,
  SET_TRANSLATE_X_FOR_ACTIVE_LAYER,
  SET_TRANSLATE_Y_FOR_ACTIVE_LAYER,
  ACTIVATE_EFFECT_FOR_ACTIVE_LAYER,
  DEACTIVATE_EFFECTS_FOR_ACTIVE_LAYER,
  SET_TEXT_ALIGNMENT_FOR_ACTIVE_LAYER,
  RESET_TEXT_LAYER_STYLES,
  ACTIVATE_THEME_BY_ID,
  RESET_IMAGE_LAYER_STYLES,
  RESET_IMAGE_LAYER_TRANSFORMATIONS,
  SET_TOOLTIPS_TO_VISIBLE,
  SET_TOOLTIPS_TO_NOT_VISIBLE,
  SET_TOOLTIP_PROPERTIES,
  SET_PREVIEW_VISIBILITY,
  SET_PRINT_MODE_TO_TRUE,
  SET_SCREENSHOT_MODE_TO_TRUE,
  SET_IMAGE_LAYER_URL,
  SET_IMAGE_URL_FOR_SELECTED_LAYER,
  SET_PREVIEW_IMAGES,
  SET_PREVIEW_IMAGES_LOADED,
  ADD_USER_IMAGE,
  GENERATE_SESSION_ID,
  SET_WAITING_FOR_PDF,
  SET_PDF_URL,
  SET_CART_IMAGE_URL,
  SET_TEXTS,
  SET_LAYOUT_MODE,
  SET_LAYER_DIMENSIONS,
  TAKE_CARD_OBJECT_SNAPSHOT,
  RESTORE_CARD_OBJECT_SNAPSHOT,
  DELETE_ACTIVE_LAYER,
  DUPLICATE_ACTIVE_LAYER,
  CREATE_TEXT_LAYER,
  CREATE_FOTO_LAYER,
  SET_SCALE,
  SET_PAPER_ID,
  SET_WINDOW_DIMENSIONS,
} from '@/store/mutation-types';

/* eslint-disable no-param-reassign */
export default {
  /**
   * Loads a greeting card and sets kartendesigner to visible
   * @param {Object} state
   * @param {Object} newCard - The card object
   */
  [SET_CARD](state, newCard) {
    const newCardClone = cloneDeep(newCard);
    Vue.set(state, 'card', newCardClone);
    state.tmp.kartendesignerVisible = true;
  },

  [SET_KARTENDESIGNER_VISIBILITY](state, isVisible) {
    state.tmp.kartendesignerVisible = isVisible;
  },

  /**
   * Generates a session ID, which can be used to associate this card with uploaded user images.
   * The session ID will stay the same when the card is opened from the wishlist or cart.
   * @param {Object} state
   */
  [GENERATE_SESSION_ID](state) {
    if (!state.card.sessionId) {
      state.card.sessionId = shortid.generate();
    }
  },

  /**
   * Sets the active side to the first element of the sides array.
   * Templatedesigner will always put the element first which the user should see first,
   * mostly this is the front of the card.
   * @param {Object} state
   */
  [SET_ACTIVE_SIDE_TO_FIRST_SIDE](state) {
    const { sides } = state.card;
    if (!sides.length > 0) { log.error('Card has no sides...'); }
    state.tmp.activeSideId = sides[0].id;
  },

  [SET_ACTIVE_SIDE_BY_ID](state, sideId) {
    state.tmp.activeSideId = sideId;
    state.tmp.activeLayerId = '';
  },

  [SET_ACTIVE_LAYER_BY_ID](state, layerId) {
    // const activeSide = state.card.sides.find(s => s.id === state.tmp.activeSideId);
    // if (!activeSide) { log.error('No active side'); }
    state.tmp.activeLayerId = layerId;
  },

  [SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT](state, boundingClientRect) {
    state.tmp.activeLayerBoundingClientRect = boundingClientRect;
  },

  [SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT_TO_NONE](state) {
    state.tmp.activeLayerBoundingClientRect = {};
  },

  [SET_ACTIVE_LAYER_TO_NONE](state) {
    state.tmp.activeLayerId = '';
    state.tmp.activeLayerBoundingClientRect = null;
  },

  /*
   * TEXT LAYER MUTATIONS
   */

  [SET_FONT_SIZE_FOR_ACTIVE_LAYER](state, fontSize) {
    // const activeSide = state.card.sides.find(c => c.id === state.tmp.activeSideId);
    // const activeSide = getterHelpers.getActiveSide(state);
    // if (!activeSide) { log.error('There is no active side!'); }
    // const activeLayer = activeSide.layers.find(l => l.id === state.tmp.activeLayerId);
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer!'); }
    if (!activeLayer.type === 'text') { log.error('Layer to set font size on is no text layer!'); }
    // We need to store the default for when we want to reset the text layer to its defaults
    if (!activeLayer.fontSizeDefault) { activeLayer.fontSizeDefault = activeLayer.fontSize; }
    activeLayer.fontSize = fontSize;
  },

  [SET_TEXT_FOR_ACTIVE_LAYER](state, text) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('There is no active layer!');
      return;
    }
    activeLayer.text = text;
  },

  [SET_LINE_HEIGHT_FOR_ACTIVE_LAYER](state, lineHeight) {
    log.debug(`Setting line height of active layer to ${lineHeight}`);
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer!'); }
    if (!activeLayer.type === 'text') { log.error('Layer to set line height on is no text layer!'); }
    // We need to store the default for when we want to reset the text layer to its defaults
    // Because this property might not exist from the beginning (original card definition),
    // we must use Vue.set to make sure reactivity keeps working.
    if (!activeLayer.lineHeightDefault) { Vue.set(activeLayer, 'lineHeightDefault', activeLayer.lineHeight); }
    Vue.set(activeLayer, 'lineHeight', lineHeight);
  },

  [SET_TEXT_FOR_ACTIVE_LAYER](state, text) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('There is no active layer!');
      return;
    }
    activeLayer.text = text;
  },

  [SET_FONT_NAME_FOR_ACTIVE_LAYER](state, fontName) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is not active layer, cannot set font name'); }
    // activeLayer.fontName = fontName;
    if (!activeLayer.fontNameDefault) { activeLayer.fontNameDefault = activeLayer.fontName; }
    Vue.set(activeLayer, 'fontName', fontName);
  },

  [SET_FONT_WEIGHT_FOR_ACTIVE_LAYER](state, fontWeight) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is not active layer, cannot set font weight'); }
    if (!activeLayer.fontWeightDefault) {
      activeLayer.fontWeightDefault = activeLayer.fontWeight;
    }
    Vue.set(activeLayer, 'fontWeight', fontWeight);
  },

  [SET_FONT_STYLE_FOR_ACTIVE_LAYER](state, fontStyle) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is not active layer, cannot set font style'); }
    if (!activeLayer.fontStyleDefault) { activeLayer.fontStyleDefault = activeLayer.fontStyle; }
    Vue.set(activeLayer, 'fontStyle', fontStyle);
  },

  [SET_FONT_COLOR_FOR_ACTIVE_LAYER](state, color) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set font color'); }
    if (!activeLayer.colorDefault) { activeLayer.colorDefault = activeLayer.color; }
    Vue.set(activeLayer, 'color', color);
  },

  /**
   * Sets the alignment for a text layer. Both, the position withing the container,
   * as well as the text-alignment (left, center, right)
   * See directions.js
   * @param {Object} state
   * @param {Object} param1 e.g. { x: directions.LEFT, y: directions.TOP }
   */
  [SET_TEXT_ALIGNMENT_FOR_ACTIVE_LAYER](state, { x, y }) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    // TODO: IMPORTANT: For each mutation, it can be the case that there
    // is no value in the card-object,
    // in this case it must be set from a global standard
    if (!activeLayer.alignmentXDefault) {
      activeLayer.alignmentXDefault = activeLayer.alignmentX;
    }
    if (!activeLayer.alignmentYDefault) {
      activeLayer.alignmentYDefault = activeLayer.alignmentY;
    }
    Vue.set(activeLayer, 'alignmentX', x);
    Vue.set(activeLayer, 'alignmentY', y);
  },
  [RESET_TEXT_LAYER_STYLES](state) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('Could not reset text styles, no active layer!');
      return;
    }
    if (activeLayer.fontNameDefault) { activeLayer.fontName = activeLayer.fontNameDefault; }
    if (activeLayer.fontSizeDefault) { activeLayer.fontSize = activeLayer.fontSizeDefault; }
    if (activeLayer.lineHeightDefault) { activeLayer.lineHeight = activeLayer.lineHeightDefault; }
    if (activeLayer.fontStyleDefault) { activeLayer.fontStyle = activeLayer.fontStyleDefault; }
    if (activeLayer.fontWeightDefault) { activeLayer.fontWeight = activeLayer.fontWeightDefault; }
    if (activeLayer.fontWeightDefault) { activeLayer.fontWeight = activeLayer.fontWeightDefault; }
    if (activeLayer.alignmentXDefault) { activeLayer.alignmentX = activeLayer.alignmentXDefault; }
    if (activeLayer.alignmentYDefault) { activeLayer.alignmentY = activeLayer.alignmentYDefault; }
    if (activeLayer.colorDefault) { activeLayer.color = activeLayer.colorDefault; }
  },

  /*
   * IMAGE LAYER Mutations
   */

  [SET_CONTRAST_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set contrast color'); }
    Vue.set(activeLayer, 'filterContrast', value);
  },
  [SET_BRIGHTNESS_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set brightness color'); }
    Vue.set(activeLayer, 'filterBrightness', value);
  },

  [SET_SCALE_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set scale color'); }
    Vue.set(activeLayer, 'transformScale', value);
    log.debug('Scale of active layer set to: ', value);
  },
  [SET_ROTATION_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set rotation color'); }
    Vue.set(activeLayer, 'transformRotate', value);
  },
  [SET_TRANSLATE_X_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set translate X'); }
    Vue.set(activeLayer, 'transformTranslateX', value);
  },
  [SET_TRANSLATE_Y_FOR_ACTIVE_LAYER](state, value) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot set translate Y'); }
    Vue.set(activeLayer, 'transformTranslateY', value);
  },
  [ACTIVATE_EFFECT_FOR_ACTIVE_LAYER](state, effectId) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot activate effect'); }
    Vue.set(activeLayer, 'filterGrayscaleActive', effectId === 'grayscale');
    Vue.set(activeLayer, 'filterSepiaActive', effectId === 'sepia');
  },
  [DEACTIVATE_EFFECTS_FOR_ACTIVE_LAYER](state) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('There is no active layer, cannot activate effect'); }
    Vue.set(activeLayer, 'filterGrayscaleActive', false);
    Vue.set(activeLayer, 'filterSepiaActive', false);
  },
  [ADD_USER_IMAGE](state, imageUrl) {
    if (!state.tmp.userImages.includes(imageUrl)) {
      state.tmp.userImages.push(imageUrl);
    }
    window.localStorage.setItem('kartendesigner-user-images', JSON.stringify(state.tmp.userImages));
  },

  [ACTIVATE_THEME_BY_ID](state, themeId) {
    const theme = state.card.themes.find(t => t.id === themeId);
    if (!theme) {
      log.error(`Could not activate theme with ID ${themeId}, because It does not exist!`);
      return;
    }
    // when the card is printed (PDF or screenshot),
    // we cannot overwrite user-edited themable properties.
    // E.g. when the user changes the theme, then changes the font color (which is themable),
    // is will get overwritten by the theme font color if we don't look out for it here.
    const previousThemeId = state.card.activeThemeId;
    const previousTheme = state.card.themes.find(t => t.id === previousThemeId);
    // Themable customizable props are layer properties, which are both
    // themable and customizable by the user.
    // E.g. font colors can be changed by the user, and can also be part of a theme.
    // We have to take extra care of these.
    // TODO: Put into extra file, to make universally accessable.
    const THEMABLE_CUSTOMIZABLE_PROPS = [
      'color',
      'lineHeight',
    ];
    state.card.activeThemeId = themeId;
    theme.sides.forEach((themeSide) => {
      const sideId = themeSide.id;
      if (themeSide.layers) {
        const previousThemeSide = previousTheme ? previousTheme.sides.find(side => side.id === sideId) : null;
        themeSide.layers.forEach((themeLayer) => {
          const layerId = themeLayer.id;
          const layerInUse = getterHelpers.getLayerBySideIdAndLayerId(
            state,
            { sideId: themeSide.id, layerId },
          );
          const previousThemeLayer = previousThemeSide ? previousThemeSide.layers.find(layer => layer.id === layerId) : null;
          if (!layerInUse) {
            log.error(`Layer with Id ${layerId} could not be found on side ${themeSide.id}`);
            return;
          }
          Object.keys(themeLayer).forEach((key) => {
            if (key !== 'id') {
              // We need to check if this property is a default property,
              // or if it was set by the user.
              let previousThemeDefaultValue;
              if (previousThemeLayer && typeof previousThemeLayer[key] !== 'undefined') {
                previousThemeDefaultValue = previousThemeLayer[key];
              }
              let skipProperty = false;
              // Check if the previously activated theme (if any) includes the themable
              // property key.
              // If it does we need to find out if the current value in the layer differs from it.
              // This way we know if the user edited the value or not.
              // If the property (e.g "color") was edited, we need to skip it when activating the theme.
              // Otherwise the user value is lost.
              if (THEMABLE_CUSTOMIZABLE_PROPS.includes(key)
                && typeof previousThemeDefaultValue !== 'undefined'
                && previousThemeDefaultValue === themeLayer[key]) {
                skipProperty = true;
              }
              // layerInUse[key] = themeLayer[key];
              // if this key does not exist already we need to set it explicitly,
              // so it is reactive
              if (!skipProperty) {
                Vue.set(layerInUse, key, themeLayer[key]);
                if (key === 'color') {
                  // layerInUse.colorDefault = themeLayer[key];
                  Vue.set(layerInUse, 'colorDefault', themeLayer[key]);
                }
              }
            }
          });
        });
      }
    });
  },
  /* eslint-disable no-prototype-builtins */
  [RESET_IMAGE_LAYER_STYLES](state) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('There is no active layer!');
      return;
    }
    if (activeLayer.type !== 'foto') {
      log.error('Layer to reset is not an image layer');
      return;
    }
    const { fotoLayerDefaults } = layerDefaults;

    // TODO: Make DRY

    if (activeLayer.hasOwnProperty('filterBrightness')) {
      activeLayer.filterBrightness = fotoLayerDefaults.filterBrightness;
    }
    if (activeLayer.hasOwnProperty('filterContrast')) {
      activeLayer.filterContrast = fotoLayerDefaults.filterContrast;
    }
    if (activeLayer.hasOwnProperty('filterGrayscaleActive')) {
      activeLayer.filterGrayscaleActive = fotoLayerDefaults.filterGrayscaleActive;
    }
    if (activeLayer.hasOwnProperty('filterSepiaActive')) {
      activeLayer.filterSepiaActive = fotoLayerDefaults.filterSepiaActive;
    }
    if (activeLayer.hasOwnProperty('transformTranslateX')) {
      activeLayer.transformTranslateX = fotoLayerDefaults.transformTranslateX;
    }
    if (activeLayer.hasOwnProperty('transformTranslateY')) {
      activeLayer.transformTranslateY = fotoLayerDefaults.transformTranslateY;
    }
    if (activeLayer.hasOwnProperty('transformRotate')) {
      activeLayer.transformRotate = fotoLayerDefaults.transformRotate;
    }
    if (activeLayer.hasOwnProperty('transformScale')) {
      activeLayer.transformScale = fotoLayerDefaults.transformScale;
    }
    // TODO: Take care of image aspect ratio, so it fills the frame!
  },

  [RESET_IMAGE_LAYER_TRANSFORMATIONS](state) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('There is no active layer!');
      return;
    }
    if (activeLayer.type !== 'foto') {
      log.error('Layer to reset is not an image layer');
      return;
    }
    const { fotoLayerDefaults } = layerDefaults;
    if (activeLayer.hasOwnProperty('transformTranslateX')) {
      activeLayer.transformTranslateX = fotoLayerDefaults.transformTranslateX;
    }
    if (activeLayer.hasOwnProperty('transformTranslateY')) {
      activeLayer.transformTranslateY = fotoLayerDefaults.transformTranslateY;
    }
    if (activeLayer.hasOwnProperty('transformRotate')) {
      activeLayer.transformRotate = fotoLayerDefaults.transformRotate;
    }
    if (activeLayer.hasOwnProperty('transformScale')) {
      activeLayer.transformScale = fotoLayerDefaults.transformScale;
    }
  },


  [SET_IMAGE_LAYER_URL](state, { sideId, layerId, url }) {
    const layer = getterHelpers.getLayerBySideIdAndLayerId(
      state,
      { sideId, layerId },
    );
    if (!layer) {
      log.error(`Layer with Id ${layerId} could not be found on side ${sideId}`);
      return;
    }
    layer.imageUrl = url;
  },
  [SET_IMAGE_URL_FOR_SELECTED_LAYER](state, imageUrl) {
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) {
      log.error('There is no active layer!');
      return;
    }
    if (!activeLayer.type === 'foto') {
      log.error('Layer to set image URL on is no image layer');
      return;
    }
    activeLayer.imageUrl = imageUrl;
  },


  /*
   * TOOLTIP MUTATIONS
   */

  [SET_TOOLTIPS_TO_VISIBLE](state) {
    state.tmp.tooltipsVisible = true;
  },
  [SET_TOOLTIPS_TO_NOT_VISIBLE](state) {
    state.tmp.tooltipsVisible = false;
  },
  /**
   * Sets one or many properties of a tooltip (typically x, y, position)
   * Finds the property by its id.
   * @param {Object} propObj e.g. { id: 'image', x: 100 }
   */
  [SET_TOOLTIP_PROPERTIES](state, propObj) {
    if (!propObj || !propObj.id) {
      log.warn('Could not set tooltip properties, returning');
      return;
    }
    const tooltip = state.tmp.tooltips.filter(tt => tt.id === propObj.id);
    Object.keys(propObj).forEach((key) => {
      tooltip[key] = propObj[key];
    });
  },

  /*
   * PREVIEW MUTATIONS
   */
  [SET_PREVIEW_VISIBILITY](state, visible) {
    state.tmp.previewVisible = visible;
  },
  [SET_PREVIEW_IMAGES](state, previews) {
    state.tmp.previewImages = { ...previews };
  },
  [SET_PREVIEW_IMAGES_LOADED]: (state, isLoaded) => {
    state.tmp.previewImagesLoaded = isLoaded;
  },

  /*
    * PRINT MODE MUTATIONS
    */
  [SET_PRINT_MODE_TO_TRUE](state) {
    state.tmp.printMode = true;
  },

  [SET_SCREENSHOT_MODE_TO_TRUE](state) {
    state.tmp.screenshotMode = true;
  },

  /*
   * LAYOUT EDITING MODE MUTATIONS
   */

  [SET_LAYOUT_MODE](state, b) {
    state.tmp.layoutModeActive = b;
  },

  /* eslint-disable object-curly-newline */
  [SET_LAYER_DIMENSIONS](state, { x, y, width, height }) {
    const layer = getterHelpers.getActiveLayer(state);
    if (layer) {
      if (typeof x !== 'undefined') { layer.x = x; }
      if (typeof y !== 'undefined') { layer.y = y; }
      if (typeof width !== 'undefined') { layer.width = width; }
      if (typeof height !== 'undefined') { layer.height = height; }
    } else {
      log.error('Cannot set dimensions of layer, layer is null!');
    }
  },

  [TAKE_CARD_OBJECT_SNAPSHOT](state) {
    state.tmp.cardObjectSnapshotAsString = JSON.stringify(state.card);
  },

  [RESTORE_CARD_OBJECT_SNAPSHOT](state) {
    const previousCard = JSON.parse(state.tmp.cardObjectSnapshotAsString);
    this.replaceState({ ...state, card: previousCard });
  },

  [DELETE_ACTIVE_LAYER](state) {
    const activeSide = getterHelpers.getActiveSide(state);
    const activeLayerId = getterHelpers.getActiveLayerId(state);
    const i = activeSide.layers.findIndex(layer => layer.id === activeLayerId);
    if (i > -1) {
      activeSide.layers.splice(i, 1);
      state.tmp.activeLayerId = null;
    }
  },

  [DUPLICATE_ACTIVE_LAYER](state) {
    const activeSide = getterHelpers.getActiveSide(state);
    const activeLayer = getterHelpers.getActiveLayer(state);
    if (!activeLayer) { log.error('Cannot clone, there is no active layer.'); return; }
    const clone = JSON.parse(JSON.stringify(activeLayer));
    // to better see the new layer we shifts its position slightly
    const shiftAmount = 2.5;
    clone.x += shiftAmount; // move right
    clone.y += shiftAmount; // move down
    // too big, reaching over the right edge
    if (clone.width + clone.x > 100) {
      clone.x -= 2 * shiftAmount; // move left instead
    }
    // still reaching over edge
    if (clone.width + clone.x > 100) {
      clone.x += shiftAmount; // original position, nothing we can do here...
    }
    // too big, reaching over the right edge
    if (clone.height + clone.y > 100) {
      clone.y -= 2 * shiftAmount; // move in top direction instead
    }
    // still reaching over edge
    if (clone.height + clone.y > 100) {
      clone.y += shiftAmount; // original position, nothing we can do here...
    }
    // we need to change the ID as well.
    // first let’s store the old one. Needed for when we change themes
    // to also change its properties.
    if (clone.originalId) {
      // we cloned a clone, so we already have a reference to the original ID
    } else {
      clone.originalId = clone.id;
    }
    clone.id = shortid.generate(); // completely random ID
    activeSide.layers.push(clone);
    state.tmp.activeLayerId = clone.id; // set new layer to be active layer
  },

  [CREATE_TEXT_LAYER](state) {
    let layer = layerInit.getBaseLayer();
    layer = layerInit.initTextLayer(layer);
    const activeSide = getterHelpers.getActiveSide(state);
    if (!activeSide) { log.error('Active Side is null!'); return; }
    activeSide.layers.push(layer);
    Vue.set(activeSide, 'layers', activeSide.layers);
    state.tmp.activeLayerId = layer.id; // set new layer to be active layer
  },

  [CREATE_FOTO_LAYER](state) {
    let layer = layerInit.getBaseLayer();
    layer = layerInit.initFotoLayer(layer);
    const activeSide = getterHelpers.getActiveSide(state);
    if (!activeSide) { log.error('Active Side is null!'); return; }
    activeSide.layers.push(layer);
    state.tmp.activeLayerId = layer.id; // set new layer to be active layer
  },

  /*
   * Other Mutations
   */
  [SET_WAITING_FOR_PDF](state, b) {
    state.tmp.waitingForPdf = b;
  },
  [SET_PDF_URL](state, url) {
    Vue.set(state.card, 'pdfUrl', url);
    log.debug('PDF URL set: ', state.card.pdfUrl);
    log.debug('Full URL: ', `${process.env.VUE_APP_IMAGE_SERVER}${state.card.pdfUrl}`);
  },
  [SET_CART_IMAGE_URL](state, url) {
    Vue.set(state.card, 'cartImageUrl', url);
  },
  [SET_TEXTS](state, texts) {
    state.tmp.texts = { ...state.tmp.texts, ...texts };
  },
  [SET_SCALE](state, scale) {
    state.tmp.scale = scale;
  },
  [SET_PAPER_ID](state, paperId) {
    state.card.paperId = paperId;
  },
  [SET_WINDOW_DIMENSIONS](state, { width, height }) {
    state.tmp.windowWidth = width;
    state.tmp.windowHeight = height;
  },
};
